import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SegmentedProps<T extends string> {
    value: T
    options: { value: T, label: string, disabled?: boolean }[]
    onChange: (value: T) => void
    label: string
    className?: string
    size?: 'sm' | 'md'
}

/** Small mutually exclusive toggle, e.g. MXN / USD or Actual / Target / Gap. */
export const Segmented = <T extends string,>({ value, options, onChange, label, className, size = 'sm' }: SegmentedProps<T>): ReactNode => {
    return (
        <div role='radiogroup' aria-label={label} className={cn('inline-flex items-center gap-0.5 rounded-xl border border-border bg-muted/60 p-0.5', className)}>
            {options.map(option => {
                const selected = option.value === value

                return (
                    <button
                        key={option.value}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        disabled={option.disabled}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'rounded-[10px] font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                            size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-[13px]',
                            selected ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}
